import { MinimalErrorTracker } from '../../shared/log.js';

const DEFAULT_OPTIONS = {
  capture: false,
  passive: false,
  dedupeWindowMs: 150,
  getDedupeKey: null,
  onlyPrimary: true,
  preventDefault: false,
  shouldProcess: null,
};

function normalizeOptions(options = {}) {
  return {
    ...DEFAULT_OPTIONS,
    ...options,
  };
}

function shouldProcessEvent(event, options, state) {
  if (typeof options.shouldProcess === 'function' && !options.shouldProcess(event, state)) {
    return false;
  }

  if (options.onlyPrimary && !event.isPrimary) {
    return false;
  }

  if (state.activePointerId !== null && state.activePointerId !== event.pointerId) {
    return false;
  }

  return true;
}

function updateDedupeState(event, options, state) {
  if (typeof options.getDedupeKey !== 'function') {
    return false;
  }

  const key = options.getDedupeKey(event, state);
  if (!key) {
    state.lastReleaseKey = null;
    state.lastReleaseTime = 0;
    return false;
  }

  const now = performance.now();
  if (state.lastReleaseKey === key && now - state.lastReleaseTime < options.dedupeWindowMs) {
    return true;
  }

  state.lastReleaseKey = key;
  state.lastReleaseTime = now;
  return false;
}

function applyPreventDefault(event, options) {
  if (options.preventDefault && typeof event.preventDefault === 'function') {
    event.preventDefault();
  }
}

function setPointerCaptureSafe(target, pointerId) {
  if (typeof target.setPointerCapture !== 'function') return;
  try {
    target.setPointerCapture(pointerId);
  } catch (error) {
    MinimalErrorTracker.info('INPUT', 'setPointerCapture failed', error);
  }
}

function releasePointerCaptureSafe(target, pointerId) {
  if (typeof target.releasePointerCapture !== 'function') return;
  try {
    target.releasePointerCapture(pointerId);
  } catch (error) {
    MinimalErrorTracker.info('INPUT', 'releasePointerCapture failed', error);
  }
}

function createPointerHandlers(target, handlers, options, state) {
  const handlePointerDown = (event) => {
    if (!shouldProcessEvent(event, options, state)) {
      return;
    }

    applyPreventDefault(event, options);

    // onDownハンドラーがfalseを返した場合は、イベントを受理しない
    const accepted = handlers.onDown?.(event, state);
    if (accepted !== false) {
      state.activePointerId = event.pointerId;
      setPointerCaptureSafe(target, event.pointerId);
    } else {
      // 明示的に拒否された場合はactivePointerIdをクリア
      state.activePointerId = null;
    }
  };

  const handlePointerMove = (event) => {
    if (!shouldProcessEvent(event, options, state)) {
      return;
    }

    applyPreventDefault(event, options);
    handlers.onMove?.(event, state);
  };

  const handlePointerUp = (event) => {
    if (!shouldProcessEvent(event, options, state)) {
      return;
    }

    applyPreventDefault(event, options);
    releasePointerCaptureSafe(target, event.pointerId);
    state.activePointerId = null;

    if (updateDedupeState(event, options, state)) {
      return;
    }

    handlers.onUp?.(event, state);
  };

  const handlePointerCancel = (event) => {
    if (!shouldProcessEvent(event, options, state)) {
      return;
    }

    releasePointerCaptureSafe(target, event.pointerId);
    state.activePointerId = null;
    handlers.onCancel?.(event, state);
  };

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  };
}

export function addPointerListeners(target, handlers = {}, rawOptions = {}) {
  if (!target) {
    MinimalErrorTracker.warn('INPUT', 'addPointerListeners called without a target');
    return () => {};
  }

  const options = normalizeOptions(rawOptions);
  const listenerOptions = { capture: options.capture, passive: options.passive };

  const state = {
    activePointerId: null,
    lastReleaseKey: null,
    lastReleaseTime: 0,
  };

  const { handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel } =
    createPointerHandlers(target, handlers, options, state);

  target.addEventListener('pointerdown', handlePointerDown, listenerOptions);
  target.addEventListener('pointermove', handlePointerMove, listenerOptions);
  target.addEventListener('pointerup', handlePointerUp, listenerOptions);
  target.addEventListener('pointercancel', handlePointerCancel, listenerOptions);

  return function removePointerListeners() {
    target.removeEventListener('pointerdown', handlePointerDown, listenerOptions);
    target.removeEventListener('pointermove', handlePointerMove, listenerOptions);
    target.removeEventListener('pointerup', handlePointerUp, listenerOptions);
    target.removeEventListener('pointercancel', handlePointerCancel, listenerOptions);
  };
}

export function resolveBoardCellFromPointerEvent(event, boardElement) {
  if (!event || !boardElement) {
    return null;
  }

  const doc = boardElement.ownerDocument || document;
  const element = doc.elementFromPoint(event.clientX, event.clientY);
  if (!element) {
    return null;
  }

  const td = element.closest?.('td');
  if (!td || !boardElement.contains(td)) {
    return null;
  }

  const row = td.parentElement?.rowIndex;
  const col = td.cellIndex;

  if (typeof row !== 'number' || typeof col !== 'number' || row < 0 || col < 0) {
    return null;
  }

  return { row, col, cell: td };
}
